import { Link } from "react-router-dom";

interface Stat {
  value: string;
  label: string;
}

interface StatsSplitProps {
  headline: string;
  description: string;
  stats: Stat[];
  image?: string;
  imageAlt?: string;
  cta?: { text: string; href: string };
  reverse?: boolean;
}

const StatsSplit = ({
  headline,
  description,
  stats,
  image,
  imageAlt = "",
  cta,
  reverse = false,
}: StatsSplitProps) => {
  const textBlock = (
    <div className="space-y-6">
      <h2 className="font-headline text-2xl sm:text-3xl md:text-4xl text-foreground leading-tight">
        {headline}
      </h2>
      <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
        {description}
      </p>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-6 pt-4">
        {stats.map((stat, i) => (
          <div key={i}>
            <span className="block font-headline text-3xl md:text-4xl text-accent leading-none">
              {stat.value}
            </span>
            <span className="block font-body text-xs uppercase tracking-widest text-muted-foreground mt-2">
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {cta && (
        <div className="pt-2">
          <Link
            to={cta.href}
            className="inline-block font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
          >
            {cta.text} →
          </Link>
        </div>
      )}
    </div>
  );

  const visualBlock = image ? (
    <div className="relative aspect-[4/5] overflow-hidden">
      <img
        src={image}
        alt={imageAlt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  ) : (
    <div className="bg-secondary on-dark aspect-[4/5] flex items-center justify-center">
      <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
        Visual Placeholder
      </span>
    </div>
  );

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
      {textBlock}
      {visualBlock}
    </div>
  );
};

export default StatsSplit;
